import React, { useState, useEffect } from 'react';
import { useUserGroups } from 'hooks/useUserGroups';
import { usePinProtection } from 'hooks/usePinProtection';
import PinValidationModal from 'app/containers/PinValidationModal';

interface Props {
  children: React.ReactNode;
}

const PinProtectedRoute: React.FC<Props> = ({ children }) => {
  const { user, isLoading } = useUserGroups();
  const { checkGracePeriod, saveGracePeriod, cleanupExpiredGracePeriod } =
    usePinProtection();

  // Local state: maintains access as long as component is mounted
  const [isPinValidated, setIsPinValidated] = useState(false);

  // Check grace period on component mount
  useEffect(() => {
    // Clean up expired grace periods first
    cleanupExpiredGracePeriod();

    // If user has PIN protection enabled and is within grace period
    if (user?.pin && user.id && checkGracePeriod(user.id)) {
      setIsPinValidated(true);
    }
  }, [user, checkGracePeriod, cleanupExpiredGracePeriod]);

  // Show loading while user data is being fetched
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Vérification des permissions...</div>
      </div>
    );
  }

  // If user doesn't require PIN protection, grant access directly
  if (!user?.pin) {
    return <>{children}</>;
  }

  // If PIN is required but not validated, show validation modal
  if (!isPinValidated) {
    return (
      <PinValidationModal
        onValidation={() => {
          // Set local state to maintain access while component is mounted
          setIsPinValidated(true);

          // Save grace period for future visits (15 minutes)
          if (user.id) {
            saveGracePeriod(user.id);
          }
        }}
        onCancel={() => {
          // Redirect to home page if user cancels
          window.location.href = '/';
        }}
      />
    );
  }

  // PIN is validated, render children
  return <>{children}</>;
};

export default PinProtectedRoute;
